import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useVirtualizer } from "@tanstack/react-virtual"
import { format } from "date-fns/format"
import { ChevronDown, ChevronUp, Dumbbell, ListFilter, Search } from "lucide-react"
import { useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { Input } from "../components/ui/input"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select"
import { Skeleton } from "../components/ui/skeleton"
import { apiFetch } from "../utils/apiFetch"

export const Route = createFileRoute("/_app/exercises")({
  component: Exercises,
})

const ROW_HEIGHT_COLLAPSED = 72
const ROW_HEIGHT_EXPANDED = 220

type TExerciseListItem = {
  exerciseId: string
  userId?: string
  name: string
  dateCreated: string
  isPublic: boolean
  muscleGroup: string
  maxWeightLifted: { maxWeight: number | null; reps: number | null }
  lastPerformedDate: string | null
  lastWorkoutName: string | null
  lastWorkoutDetails: Array<{ weight: number; reps: number; order: number }>
}

const MUSCLE_GROUPS = [
  "All",
  "Chest",
  "Back",
  "Back (Lats)",
  "Shoulders",
  "Side Delts",
  "Biceps",
  "Triceps",
  "Quads",
  "Hamstrings",
  "Glutes",
  "Calves",
  "Abs",
  "Forearm Extensors",
  "Forearm Flexors",
  "Neck",
  "Other",
  "unknown",
]

export default function Exercises() {
  const queryClient = useQueryClient()
  const { data: exercises, isLoading } = useQuery<TExerciseListItem[]>({
    queryKey: ["exercises"],
    queryFn: () => apiFetch<TExerciseListItem[]>("/v1/exercises"),
  })

  const { mutate: updateExercise } = useMutation({
    mutationFn: ({ exerciseId, muscleGroup }: { exerciseId: string; muscleGroup: string }) =>
      apiFetch(`/v1/exercises/${exerciseId}`, {
        method: "PUT",
        body: JSON.stringify({ muscleGroup }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exercises"] })
      toast.success("Muscle group updated")
    },
    onError: () => {
      toast.error("Failed to update muscle group")
    },
  })

  const [search, setSearch] = useState("")
  const [muscleGroupFilter, setMuscleGroupFilter] = useState("All")
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const parentRef = useRef<HTMLDivElement>(null)

  const filtered = useMemo(() => {
    if (!exercises) {
      return []
    }
    return exercises.filter((ex) => {
      const matchesSearch = search === "" || ex.name.toLowerCase().includes(search.toLowerCase())
      const matchesGroup = muscleGroupFilter === "All" || ex.muscleGroup === muscleGroupFilter
      return matchesSearch && matchesGroup
    })
  }, [exercises, search, muscleGroupFilter])

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => (filtered[index].exerciseId === expandedId ? ROW_HEIGHT_EXPANDED : ROW_HEIGHT_COLLAPSED),
    overscan: 5,
  })

  const uniqueMuscleGroups = useMemo(() => {
    if (!exercises) {
      return MUSCLE_GROUPS
    }
    const groups = new Set(exercises.map((ex) => ex.muscleGroup))
    return ["All", ...MUSCLE_GROUPS.filter((g) => g !== "All" && groups.has(g))]
  }, [exercises])

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Dumbbell className="h-5 w-5" />
            Exercises
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search exercises..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={muscleGroupFilter} onValueChange={setMuscleGroupFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <ListFilter className="mr-2 h-4 w-4" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {uniqueMuscleGroups.map((group) => (
                    <SelectItem key={group} value={group}>
                      {group === "unknown" ? "Unassigned" : group}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-[72px] w-full" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center">
              <Dumbbell className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
              <p className="text-muted-foreground">
                {search || muscleGroupFilter !== "All" ? "No exercises match your filters." : "No exercises yet."}
              </p>
            </div>
          ) : (
            <div ref={parentRef} className="h-[calc(100vh-320px)] min-h-[400px] overflow-auto rounded-md border">
              <div style={{ height: `${virtualizer.getTotalSize()}px`, position: "relative", width: "100%" }}>
                {virtualizer.getVirtualItems().map((virtualRow) => {
                  const ex = filtered[virtualRow.index]
                  const isExpanded = ex.exerciseId === expandedId
                  return (
                    <div
                      key={ex.exerciseId}
                      data-index={virtualRow.index}
                      ref={virtualizer.measureElement}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      <ExerciseRow
                        exercise={ex}
                        isExpanded={isExpanded}
                        onToggle={() => setExpandedId(isExpanded ? null : ex.exerciseId)}
                        onUpdateMuscleGroup={(muscleGroup) =>
                          updateExercise({ exerciseId: ex.exerciseId, muscleGroup })
                        }
                      />
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function ExerciseRow({
  exercise,
  isExpanded,
  onToggle,
  onUpdateMuscleGroup,
}: {
  exercise: TExerciseListItem
  isExpanded: boolean
  onToggle: () => void
  onUpdateMuscleGroup: (muscleGroup: string) => void
}) {
  const [isEditingGroup, setIsEditingGroup] = useState(false)
  const [editGroup, setEditGroup] = useState(exercise.muscleGroup)

  const displayGroup = exercise.muscleGroup === "unknown" ? "Unassigned" : exercise.muscleGroup
  const lastDate = exercise.lastPerformedDate ? format(exercise.lastPerformedDate, "MMM d, yyyy") : null

  return (
    <div className="border-b">
      <button
        type="button"
        onClick={onToggle}
        className="flex h-[72px] w-full items-center gap-3 px-4 text-left hover:bg-muted/50"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-sm">{exercise.name}</span>
            {exercise.isPublic && (
              <span className="shrink-0 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                Public
              </span>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-muted-foreground text-xs">
            {exercise.maxWeightLifted.maxWeight != null ? (
              <span>Max: {exercise.maxWeightLifted.maxWeight} lbs</span>
            ) : (
              <span>No data</span>
            )}
            <span>·</span>
            <span>{displayGroup}</span>
            {lastDate && (
              <>
                <span>·</span>
                <span>Last: {lastDate}</span>
              </>
            )}
          </div>
        </div>
        {isExpanded ? <ChevronUp className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
      </button>

      {isExpanded && (
        <div className="border-t bg-muted/20 px-4 py-3">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <p className="font-medium text-xs">Muscle Group</p>
              {isEditingGroup ? (
                <div className="flex items-center gap-2">
                  <Select
                    value={editGroup}
                    onValueChange={(value) => {
                      setEditGroup(value)
                      onUpdateMuscleGroup(value)
                      setIsEditingGroup(false)
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {MUSCLE_GROUPS.filter((g) => g !== "All").map((group) => (
                          <SelectItem key={group} value={group}>
                            {group === "unknown" ? "Unassigned" : group}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setIsEditingGroup(false)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsEditingGroup(true)}
                  className="rounded bg-muted px-2 py-1 text-xs hover:bg-muted/80"
                >
                  {displayGroup}
                </button>
              )}
            </div>

            <div className="space-y-1">
              <p className="font-medium text-xs">Last Workout</p>
              {exercise.lastWorkoutDetails.length > 0 ? (
                <div className="space-y-1">
                  {exercise.lastWorkoutName && (
                    <p className="text-muted-foreground text-xs">{exercise.lastWorkoutName}</p>
                  )}
                  {exercise.lastWorkoutDetails.map((detail, i) => (
                    <div key={i} className="text-xs">
                      Set {detail.order}: {detail.weight} lbs × {detail.reps} reps
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-xs">No workouts recorded</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
